/** @format */

import React, { useState } from "react";
import Select from "react-select";
import cn from "classnames";
import style from "./index.module.scss";
import "./select-custom.scss";
import PlusIcon from "../../shared/icons/PlusIcon";

const selectStyles = {
  option: (styles, { data, isDisabled, isFocused, isSelected, ...rest }) => {
    return {
      ...styles,
    };
  },
  menu: (provided) => ({
    ...provided,
    paddingBottom: 0
  }),
  menuList: (provided) => ({
    ...provided,
    paddingBottom: 0,
  })
};

const portalStyles = {
  ...selectStyles,
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999
  })
};

const CLASS_NAMES = {
  name: "select-custom-wrapper",
  root: "custom-select",
};

const getOptionDisplayName = (option, keyName) => {
  const name = option[keyName];
  if (name === null || name === undefined || name === '') {
    return "Без названия";
  }
  return name;
};

const CustomSelect = ({
  placeholder = "Выберите вариант",
  keyId = "id",
  keyName = "name",
  iconKey = null,
  isDisabled = false,
  returnObject = false,
  bgGray = false,
  bgWhite = false,
  defaultValue = null,
  IconStart = null,
  options = [],
  onSubmit,
  onNoOptionsClick,
  showNoOptionsMessage = false,
  labelText = "",
  inputId,
  labelClassName = "",
  labelSmall = false,
  errorText = "",
  showAddCategoryButton = false,
  onAddCategoryClick,
  addCategoryButtonText = "Добавить категорию",
  onOptionsUpdate,
  usePortal = false,
  menuPlacement = "auto",
  }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const safeOptions = Array.isArray(options) ? options : [];

  const onOptionChangeHandler = (option) => {
    if (returnObject) {
      onSubmit(option);
    } else {
      onSubmit(option[keyId]);
    }
  };

  const filteredOptions = safeOptions.filter(option => {
    const optionName = getOptionDisplayName(option, keyName);
    return optionName.toLowerCase().includes(searchValue.toLowerCase());
  });

  let extendedOptions = [...filteredOptions];

  if (showAddCategoryButton && !isAddingCategory) {
    console.log('Adding category button - no results found for:', searchValue);
    extendedOptions.unshift({
      [keyId]: 'add-new-category',
      [keyName]: isAddingCategory ? "Добавление..." : addCategoryButtonText,
      isAddButton: true,
      buttonType: 'category',
      searchValue: searchValue
    });
  }

  if (showNoOptionsMessage) {
    extendedOptions.push({
      [keyId]: 'add-new-city',
      [keyName]: 'Добавить город',
      isAddButton: true,
      buttonType: 'city',
      searchValue: searchValue,
    });
  }

  const noOptionsMessage = ({ inputValue }) => {
    if (showAddCategoryButton && inputValue && filteredOptions.length === 0) {
      return null;
    }
    return "Нет вариантов";
  };

  const formatOptionLabel = (option) => {
    if (option.isAddButton) {
      return (
        <div
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (option.buttonType === 'city' && onNoOptionsClick) {
              onNoOptionsClick((option.searchValue || "").trim());
            } else if (option.buttonType === 'category' && onAddCategoryClick && !isAddingCategory) {
              setIsAddingCategory(true);
              try {
                const newCategory = await onAddCategoryClick(option.searchValue || searchValue);

                if (newCategory && onOptionsUpdate) {
                  const updatedOptions = [...safeOptions, newCategory];
                  onOptionsUpdate(updatedOptions);

                  setSearchValue("");

                  setTimeout(() => {
                    onOptionChangeHandler(newCategory);
                  }, 100);
                }
              } catch (error) {
                console.error('Ошибка при добавлении категории:', error);
              } finally {
                setIsAddingCategory(false);
              }
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: isAddingCategory ? 'default' : 'pointer',
            opacity: isAddingCategory ? 0.6 : 1
          }}
        >
          <PlusIcon style={{ marginRight: '8px' }} /> {option[keyName]}
        </div>
      );
    }
    
    if (iconKey && option[iconKey]) {
      const IconComponent = option[iconKey];
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconComponent style={{ width: '24px', height: '24px', flexShrink: 0 }} />
          <span>{getOptionDisplayName(option, keyName)}</span>
        </div>
      );
    }
    
    return getOptionDisplayName(option, keyName);
  };

  const getOptionLabel = (option) => {
    if (option.isAddButton) {
      return option[keyName];
    }
    return getOptionDisplayName(option, keyName);
  };

  return (
    <div
      className={cn(CLASS_NAMES.root, style.select_control, {
        [CLASS_NAMES.root + "__bgGray"]: bgGray,
        [CLASS_NAMES.root + "__bgWhite"]: bgWhite,
        [CLASS_NAMES.root + "__withIcon"]: IconStart,
        [CLASS_NAMES.root + "__isError"]: errorText,
      })}
    >
      <label
        htmlFor={inputId}
        className={cn(style.labelInp, labelClassName, {
          [style.labelSmall]: labelSmall,
          [style.focusedLabel]: isFocused,
          [style.labelError]: errorText,
        })}
      >
        {labelText}
      </label>
      <div
        className={cn(CLASS_NAMES.root + "__icon", {
          [CLASS_NAMES.root + "__icon_selected"]: defaultValue,
        })}
        style={{ display: IconStart ? "" : "none" }}
      >
        {IconStart}
      </div>
      <Select
        inputId={inputId}
        defaultValue={defaultValue}
        onChange={(option) => {
          if (!option.isAddButton) {
            onOptionChangeHandler(option);
          }
        }}
        onInputChange={(inputValue) => {
          setSearchValue(inputValue);
        }}
        placeholder={placeholder}
        className={cn(CLASS_NAMES.name, {
          [style.input_errorState]: errorText,
        })}
        classNamePrefix={CLASS_NAMES.name + "__content"}
        getOptionLabel={getOptionLabel}
        getOptionValue={(option) => option[keyId]}
        formatOptionLabel={formatOptionLabel}
        styles={usePortal ? portalStyles : selectStyles}
        menuPlacement={menuPlacement}
        {...(usePortal && {
          menuPortalTarget: document.body,
          menuPosition: "fixed",
        })}
        isOptionSelected={(option) => {
          if (!defaultValue || option.isAddButton) {
            return false;
          }
          return defaultValue[keyId] === option[keyId];
        }}
        value={defaultValue}
        options={extendedOptions}
        isDisabled={isDisabled}
        noOptionsMessage={noOptionsMessage}
        onMenuOpen={() => setIsFocused(true)}
        onMenuClose={() => setIsFocused(false)}
        isSearchable={true}
        filterOption={() => true}
      />
      {errorText && <div className={style.errorText}>{errorText}</div>}
    </div>
  );
};

export default CustomSelect;
